<i18n>
[
    "browse__sortBy--title",
]
</i18n>

<template>
    <div class="c-sort">
        <div
            v-if="showOnDesktop"
            class="c-sort__desktop"
        >
            <BaseSelect
                :modelValue="selectedSortOption"
                :name="'sort-option'"
                :labelText="$t('browse__sortBy--title')"
                :options="formattedSortOptions"
                :selectType="SELECT_STYLE_TYPES.SORT"
                class="o-text--caption"
                @update:model-value="selectSortOption"
            />
        </div>
        <div v-else class="c-sort__mobile">
            <p class="o-text--label u-text--gray u-spacer--1pt5">
                {{ $t('browse__sortBy--title') }}
            </p>
            <BaseRadioButtonGroup
                v-if="formattedSortOptions.length > 1"
                :id="'sort-option'"
                :modelValue="selectedSortOption"
                :name="'sort-option'"
                :radioButtonItems="formattedSortOptions"
                :checkedItem="selectedSortOption"
                :styleType="BUTTON_STYLE_TYPES.INLINE_TILE_NOWRAP"
                @update:model-value="selectSortOption"
            />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useActions } from '~coreModules/core/js/composables/vuexHelpers';

import { BUTTON_STYLE_TYPES, SELECT_STYLE_TYPES } from '~coreModules/core/js/constants';
import { CATALOG_QUERY_PAGE, CATALOG_QUERY_SORT } from '~coreModules/catalog/js/catalog-constants';
import { CATALOG_SORT_UPDATED } from '~coreModules/core/js/global-event-constants';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';

import BaseSelect from '~coreModules/core/components/ui/form-elements/dropdowns/BaseSelect.vue';
import BaseRadioButtonGroup from '~coreModules/core/components/ui/form-elements/radios/BaseRadioButtonGroup.vue';

const props = defineProps({
    showOnDesktop: {
        type: Boolean,
        default: true,
    },
    sortOptions: {
        type: Array,
        required: true,
    },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

const formattedSortOptions = computed(() => props.sortOptions
    .map(option => ({
        label: t(option.displayName),
        value: option.slug,
    })));

const defaultSortOption = computed(() => props.sortOptions?.[0]?.slug);
const selectedSortOption = computed(() => route.query[CATALOG_QUERY_SORT] || defaultSortOption.value);

function selectSortOption(value) {
    const newQuery = { ...route.query };

    // Remove sort param if it's the default, add if its not the defaultSortOption
    if (value === defaultSortOption.value) {
        delete newQuery[CATALOG_QUERY_SORT];
    } else {
        newQuery[CATALOG_QUERY_SORT] = value;
    }

    // Always delete page param for all new sort requests
    delete newQuery[CATALOG_QUERY_PAGE];

    trackGlobalEvent({
        type: CATALOG_SORT_UPDATED,
        data: { label: value },
    });

    // updating route query params will update selectedSortOption
    router.replace({ query: newQuery });
}

</script>

<style lang="scss">
    .c-sort {
        overflow-x: auto;

        &__mobile {
            label {
                text-transform: capitalize;
            }
        }

        &__desktop {
            display: none;

            @include breakpoint(medium) {
                display: block;
            }
        }
    }
</style>
